import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../supabaseClient";
import LoaderBox from "./LoaderBox";

const HomeGroups = ({ activeGroupFromSearch }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState([]);
  const [hideDataMode, setHideDataMode] = useState(false);
  const groupRefs = useRef({});

  const allGroupNames = ["A", "B", "C", "D", "E", "F", "G", "H"];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: settings } = await supabase
          .from("site_settings")
          .select("prikazi_timove")
          .eq("id", 1)
          .single();

        if (settings) {
          // Ako je localhost, uvijek prikazuj (false = nije skriveno)
          // Ako je online, sakrij ako je prikazi_timove u bazi false
          const isLocalhost =
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";
          setHideDataMode(isLocalhost ? false : !settings.prikazi_timove);
        }
        const { data: teamsData } = await supabase.from("teams").select("*");
        const { data: matchesData } = await supabase
          .from("matches")
          .select("*")
          .eq("is_completed", true)
          .neq("group_name", "Završnica");

        const stats = {};

        teamsData?.forEach((t) => {
          stats[t.id] = {
            ...t,
            played: 0,
            points: 0,
            goals_for: 0,
            goals_against: 0,
            gd: 0,
          };
        });

        matchesData?.forEach((m) => {
          const tA = stats[m.team_a_id];
          const tB = stats[m.team_b_id];

          if (tA && tB) {
            tA.played += 1;
            tB.played += 1;
            tA.goals_for += m.scorea || 0;
            tA.goals_against += m.scoreb || 0;
            tB.goals_for += m.scoreb || 0;
            tB.goals_against += m.scorea || 0;

            if (m.scorea > m.scoreb) tA.points += 3;
            else if (m.scorea < m.scoreb) tB.points += 3;
            else {
              tA.points += 1;
              tB.points += 1;
            }
          }
        });

        const processedTeams = Object.values(stats).map((t) => ({
          ...t,
          gd: t.goals_for - t.goals_against,
        }));

        setTeams(processedTeams);
      } catch (err) {
        console.error("Greška:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (activeGroupFromSearch) {
      setOpenGroups((prev) =>
        prev.includes(activeGroupFromSearch)
          ? prev
          : [...prev, activeGroupFromSearch],
      );

      setTimeout(() => {
        const element = groupRefs.current[activeGroupFromSearch];
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.classList.add("highlight-group");
          setTimeout(() => element.classList.remove("highlight-group"), 2000);
        }
      }, 300);
    }
  }, [activeGroupFromSearch]);

  const toggleGroup = (groupName) => {
    setOpenGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((g) => g !== groupName)
        : [...prev, groupName],
    );
  };

  const realGroupedTeams = teams.reduce((acc, team) => {
    const group = team.group_name || "Bez grupe";
    if (!acc[group]) acc[group] = [];
    acc[group].push(team);
    return acc;
  }, {});

  const finalGroups = allGroupNames.map((name) => {
    const teamsInGroup = hideDataMode ? [] : realGroupedTeams[name] || [];

    const sortedTeamsInGroup = [...teamsInGroup].sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.gd !== a.gd) return b.gd - a.gd;
      if (b.goals_for !== a.goals_for) return b.goals_for - a.goals_for;
      return a.name.localeCompare(b.name, "hr");
    });

    // Ovdje uvijek radimo popunjavanje do 4 ekipe,
    // ali ako je hideDataMode, u sortedTeamsInGroup neće biti ničega
    // pa će svi redovi biti "placeholderi" (Ekipa 1, 2, 3, 4)
    const displayTeams = [...sortedTeamsInGroup];
    for (let i = sortedTeamsInGroup.length; i < 4; i++) {
      displayTeams.push({
        id: `empty-${name}-${i}`,
        name: `Ekipa ${i + 1}`,
        played: 0,
        goals_for: 0,
        goals_against: 0,
        points: 0,
        isPlaceholder: true,
      });
    }

    return { groupName: name, teams: displayTeams };
  });

  if (loading) return <LoaderBox text="Učitavanje tablica..." />;

  return (
    <div className="groups-container">
      {finalGroups.map(({ groupName, teams: groupTeams }) => (
        <div
          key={groupName}
          ref={(el) => (groupRefs.current[groupName] = el)}
          className={`group-card ${openGroups.includes(groupName) ? "open" : ""}`}
        >
          <div className="group-header" onClick={() => toggleGroup(groupName)}>
            <h4>Grupa {groupName}</h4>
            <i
              className={`fi ${openGroups.includes(groupName) ? "fi-rs-angle-small-up" : "fi-rs-angle-small-down"}`}
            ></i>
          </div>

          <div className="group-content">
            <div className="standings-table">
              <div className="table-head">
                <span>#</span>
                <span>EKIPA</span>
                <span>OU</span>
                <span>GOL</span>
                <span>BOD</span>
              </div>
              <div className="table-body">
                {groupTeams.map((team, tIdx) => (
                  <div
                    className={`table-body-club ${team.isPlaceholder ? "placeholder-row" : ""}`}
                    key={team.id}
                  >
                    {/* KRUG OKO BROJA: Zelena za 1 i 2, Crvena za 3 i 4 */}
                    <span
                      className={`rank-circle ${tIdx < 2 ? "pass" : "fail"}`}
                    >
                      {tIdx + 1}
                    </span>

                    <span className="team-name">{team.name}</span>
                    <span className="played">{team.played}</span>
                    <span className="ga">
                      {team.goals_for}:{team.goals_against}
                    </span>
                    <span className="pts">{team.points}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* LEGENDA NA DNU */}
      {/*       <div className="groups-legend">
        <div className="legend-item">
          <span className="dot green"></span>
          <span>Prolaz u knockout fazu</span>
        </div>
        <div className="legend-item">
          <span className="dot red"></span>
          <span>Ispadanje</span>
        </div>
      </div> */}
    </div>
  );
};

export default HomeGroups;
